/**
 * useUndoClear: undo and clear-all action hooks.
 *
 * M2.T8 per IMPLEMENTATION_PLAN §7 M2.T8 + PRD §6.1.
 *
 * - **Undo:** removes the last finalized stroke from the local stroke log
 *   and re-renders from scratch.
 * - **Clear all:** requires a 1.5-second press-and-hold confirm. The
 *   `clearHolding` boolean goes true on pointer/mousedown, false on
 *   pointer/mouseup or cancel. If held for ≥ CLEAR_HOLD_MS the clear fires.
 *
 * Both operations emit an Action to the server via the `dispatch` callback
 * so the change is recorded (STROKE_UNDO / STROKE_CLEAR) and broadcast.
 */

import { useState, useRef, useCallback } from "preact/hooks";

/** Duration in ms the clear button must be held before firing. */
export const CLEAR_HOLD_MS = 1500;

export type UndoClearCallbacks = {
  onUndo: () => void;
  onClear: () => void;
};

export type UndoClearReturn = {
  /** True while the clear button is being held down. */
  clearHolding: boolean;
  /** Called on mousedown/pointerdown on the clear button. */
  onClearStart: () => void;
  /** Called on mouseup/pointerup/pointercancel on the clear button. */
  onClearEnd: () => void;
  /** Shorthand: call onUndo directly. */
  onUndo: () => void;
};

/**
 * Returns handlers for Undo (immediate) and Clear (1.5s hold confirm).
 */
export function useUndoClear(callbacks: UndoClearCallbacks): UndoClearReturn {
  const [clearHolding, setClearHolding] = useState(false);
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cbRef = useRef(callbacks);
  cbRef.current = callbacks;

  const onClearStart = useCallback(() => {
    setClearHolding(true);
    holdTimerRef.current = setTimeout(() => {
      cbRef.current.onClear();
      setClearHolding(false);
    }, CLEAR_HOLD_MS);
  }, []);

  const onClearEnd = useCallback(() => {
    if (holdTimerRef.current !== null) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    setClearHolding(false);
  }, []);

  const onUndo = useCallback(() => {
    cbRef.current.onUndo();
  }, []);

  return { clearHolding, onClearStart, onClearEnd, onUndo };
}
